import { css, useTheme } from '@emotion/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { fontFamilies, fontSizes, radii, spacing } from '@/theme/tokens';

// --- Types ---

interface SectionStyleData {
  position: number;
  name: string;
  duration: string;
  positiveLocalStyles: string[];
  negativeLocalStyles: string[];
}

export interface AdvancedStylesPanelProps {
  positiveGlobalStyles: string[];
  negativeGlobalStyles: string[];
  sections: SectionStyleData[];
  onGlobalPositiveChange: (styles: string[]) => void;
  onGlobalNegativeChange: (styles: string[]) => void;
  onSectionPositiveChange: (position: number, styles: string[]) => void;
  onSectionNegativeChange: (position: number, styles: string[]) => void;
}

// --- Helpers ---

function stylesToText(styles: string[]): string {
  return styles.join('\n');
}

function textToStyles(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// --- Sub-components ---

interface StyleTextareaProps {
  label: string;
  value: string[];
  onChange: (styles: string[]) => void;
  minHeight?: number;
  bgColor?: string;
  borderColor?: string;
  labelColor?: string;
  helperText?: string;
  systemNote?: string;
  id: string;
}

function StyleTextarea({
  label,
  value,
  onChange,
  minHeight = 72,
  bgColor = 'transparent',
  borderColor,
  labelColor,
  helperText,
  systemNote,
  id,
}: StyleTextareaProps) {
  const theme = useTheme();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(textToStyles(e.target.value));
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.xs};
      `}
    >
      <label
        htmlFor={id}
        css={css`
          font-size: ${fontSizes.xs};
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: ${labelColor ?? theme.colors.textSecondary};
        `}
      >
        {label}
      </label>
      <textarea
        id={id}
        value={stylesToText(value)}
        onChange={handleChange}
        css={css`
          min-height: ${minHeight}px;
          padding: ${spacing.sm};
          border-radius: ${radii.md};
          border: 1px solid ${borderColor ?? theme.colors.border};
          background: ${bgColor};
          color: ${theme.colors.text};
          font-family: ${fontFamilies.ui};
          font-size: ${fontSizes.sm};
          line-height: 1.5;
          resize: vertical;
          outline: none;
          width: 100%;
          box-sizing: border-box;
          &:focus {
            border-color: ${borderColor ?? theme.colors.primary};
          }
        `}
      />
      {helperText && (
        <p
          css={css`
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
            margin: 0;
          `}
        >
          {helperText}
        </p>
      )}
      {systemNote && (
        <p
          css={css`
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
            font-style: italic;
            margin: 0;
            opacity: 0.7;
          `}
        >
          {systemNote}
        </p>
      )}
    </div>
  );
}

interface SectionCardProps {
  section: SectionStyleData;
  onPositiveChange: (position: number, styles: string[]) => void;
  onNegativeChange: (position: number, styles: string[]) => void;
}

function SectionCard({ section, onPositiveChange, onNegativeChange }: SectionCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const sectionLabel = section.name;

  return (
    <div
      css={css`
        border: 1px solid ${theme.colors.border};
        border-radius: 8px;
        overflow: hidden;
      `}
    >
      <button
        type="button"
        aria-label={sectionLabel}
        onClick={() => setExpanded((prev) => !prev)}
        css={css`
          width: 100%;
          display: flex;
          align-items: center;
          gap: ${spacing.sm};
          padding: ${spacing.xs} ${spacing.sm};
          background: transparent;
          border: none;
          cursor: pointer;
          color: ${theme.colors.text};
          font-size: ${fontSizes.sm};
          font-weight: 500;
          text-align: left;
          &:hover {
            background: ${theme.colors.surface};
          }
        `}
      >
        {expanded ? (
          <ChevronDown size={14} aria-hidden="true" />
        ) : (
          <ChevronRight size={14} aria-hidden="true" />
        )}
        <span>{sectionLabel}</span>
        <span
          css={css`
            margin-left: auto;
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
          `}
        >
          {section.duration}
        </span>
      </button>

      {expanded && (
        <div
          css={css`
            padding: ${spacing.sm} ${spacing.md} ${spacing.md};
            display: flex;
            flex-direction: column;
            gap: ${spacing.sm};
            border-top: 1px solid ${theme.colors.border};
          `}
        >
          <StyleTextarea
            id={`section-positive-${section.position}`}
            label={`Section Style for ${section.name}`}
            value={section.positiveLocalStyles}
            onChange={(styles) => onPositiveChange(section.position, styles)}
            minHeight={72}
            bgColor={`${theme.colors.primary}0a`}
            borderColor={`${theme.colors.primary}33`}
            labelColor={`${theme.colors.primary}cc`}
          />
          <StyleTextarea
            id={`section-negative-${section.position}`}
            label={`Avoid in ${section.name}`}
            value={section.negativeLocalStyles}
            onChange={(styles) => onNegativeChange(section.position, styles)}
            minHeight={48}
            bgColor={`${theme.colors.error}0a`}
            borderColor={`${theme.colors.error}33`}
            labelColor={`${theme.colors.error}cc`}
          />
        </div>
      )}
    </div>
  );
}

// --- Main Component ---

export function AdvancedStylesPanel({
  positiveGlobalStyles,
  negativeGlobalStyles,
  sections,
  onGlobalPositiveChange,
  onGlobalNegativeChange,
  onSectionPositiveChange,
  onSectionNegativeChange,
}: AdvancedStylesPanelProps) {
  const theme = useTheme();

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.md};
      `}
    >
      {/* 1. Global Style Direction (green) */}
      <StyleTextarea
        id="global-positive"
        label="Global Style Direction"
        value={positiveGlobalStyles}
        onChange={onGlobalPositiveChange}
        minHeight={72}
        bgColor={`${theme.colors.success}0f`}
        borderColor={`${theme.colors.success}33`}
        labelColor={`${theme.colors.success}cc`}
        helperText="Describe the overall sound you want across the entire composition"
      />

      {/* 3. Avoid Globally (red) */}
      <StyleTextarea
        id="global-negative"
        label="Avoid Globally"
        value={negativeGlobalStyles}
        onChange={onGlobalNegativeChange}
        minHeight={48}
        bgColor={`${theme.colors.error}0a`}
        borderColor={`${theme.colors.error}33`}
        labelColor={`${theme.colors.error}cc`}
        systemNote="System also adds: vocals, singing, lyrics (always excluded)"
      />

      {/* 4. Divider */}
      <hr
        css={css`
          border: none;
          border-top: 1px solid ${theme.colors.border};
          margin: 0;
        `}
      />

      {/* 5. Per-Section Styles */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.sm};
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              font-size: ${fontSizes.xs};
              text-transform: uppercase;
              letter-spacing: 0.08em;
              font-weight: 600;
              color: ${theme.colors.textSecondary};
            `}
          >
            Per-Section Styles
          </span>
          <span
            css={css`
              font-size: ${fontSizes.xs};
              color: ${theme.colors.textSecondary};
            `}
          >
            {sections.length} {sections.length === 1 ? 'section' : 'sections'}
          </span>
        </div>

        {sections.map((section) => (
          <SectionCard
            key={section.position}
            section={section}
            onPositiveChange={onSectionPositiveChange}
            onNegativeChange={onSectionNegativeChange}
          />
        ))}
      </div>
    </div>
  );
}
